/**
 * Notification Panel Component
 *
 * Dropdown panel displaying recent notifications, merged from the server's
 * persisted list and any socket-pushed items still only in the local store.
 * Features:
 * - Scrollable list (max 10 notifications)
 * - Each notification: icon, message, timestamp
 * - Click marks read (if unread) and navigates to its actionUrl
 * - Mark all as read
 * - View all notifications link
 * - Empty state
 */

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { formatDistanceToNow } from 'date-fns';
import { vi, enUS } from 'date-fns/locale';
import {
  Info,
  CheckCircle,
  AlertTriangle,
  XCircle,
  CreditCard,
  Server,
  Settings as SettingsIcon,
  Bell,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { Notification, NotificationType } from '@/stores/notificationStore';
import type { ResourceExpiryReminderMetadata } from '@/types/notification';

const notificationIcons: Record<NotificationType, typeof Info> = {
  info: Info,
  success: CheckCircle,
  warning: AlertTriangle,
  error: XCircle,
  billing: CreditCard,
  resource: Server,
  system: SettingsIcon,
};

const notificationColors: Record<NotificationType, string> = {
  info: 'text-info',
  success: 'text-ok',
  warning: 'text-warn',
  error: 'text-destructive',
  billing: 'text-brand',
  resource: 'text-brand-hover',
  system: 'text-muted',
};

function isResourceExpiryReminder(
  metadata: unknown,
): metadata is ResourceExpiryReminderMetadata {
  if (typeof metadata !== 'object' || metadata === null) {
    return false;
  }
  const candidate = metadata as { kind?: unknown; resources?: unknown };
  return (
    candidate.kind === 'resource_expiry_reminder' &&
    Array.isArray(candidate.resources)
  );
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** The customer calendar the backend counts reminder days on (GMT+7, no DST). */
const CUSTOMER_UTC_OFFSET_MS = 7 * 60 * 60 * 1000;

/**
 * Whole days from today to the expiry date, both on the customer calendar — the
 * same count the backend put in the reminder, but as of now rather than as of
 * the day it was sent, so a reminder opened three days later does not still
 * say "7 days". Falls back to the stored count when `expiresAt` does not parse.
 */
function currentDaysUntilExpiry(
  resource: ResourceExpiryReminderMetadata['resources'][number],
): number {
  // The value is UTC; one serialised without an offset must not be read as the viewer's local time.
  const hasOffset = /(Z|[+-]\d{2}:?\d{2})$/i.test(resource.expiresAt);
  const expiresAtMs = Date.parse(hasOffset ? resource.expiresAt : `${resource.expiresAt}Z`);
  if (Number.isNaN(expiresAtMs)) {
    return resource.daysUntilExpiry;
  }
  const expiryDay = Math.floor((expiresAtMs + CUSTOMER_UTC_OFFSET_MS) / MS_PER_DAY);
  const today = Math.floor((Date.now() + CUSTOMER_UTC_OFFSET_MS) / MS_PER_DAY);
  return expiryDay - today;
}

/**
 * Title/message for a notification as shown in the panel. Known structured
 * kinds (currently only `resource_expiry_reminder`) are rendered from
 * `metadata` via i18n; everything else falls back to the server's English
 * `title`/`message`.
 */
function useDisplayContent(
  notification: Notification,
): { title: string; message: string } {
  const { t } = useTranslation('notifications');

  if (isResourceExpiryReminder(notification.metadata)) {
    const resources = notification.metadata.resources;
    if (resources.length === 1) {
      const [resource] = resources;
      const days = currentDaysUntilExpiry(resource);
      return {
        title: t('bell.resourceExpiry.title'),
        message: singleResourceMessage(t, resource.name, days),
      };
    }
    if (resources.length > 1) {
      const soonest = Math.min(...resources.map(currentDaysUntilExpiry));
      return {
        title: t('bell.resourceExpiry.title'),
        message: manyResourcesMessage(t, resources.length, soonest),
      };
    }
  }

  return { title: notification.title, message: notification.message };
}

type Translate = TFunction<'notifications'>;

/** A date already past, or today, is said in words rather than as "in -3 days" / "in 0 days". */
function singleResourceMessage(t: Translate, name: string, days: number): string {
  if (days < 0) {
    return t('bell.resourceExpiry.messageOneExpired', { name });
  }
  if (days === 0) {
    return t('bell.resourceExpiry.messageOneToday', { name });
  }
  return t('bell.resourceExpiry.messageOne', { name, count: days });
}

function manyResourcesMessage(t: Translate, vmCount: number, soonest: number): string {
  if (soonest < 0) {
    return t('bell.resourceExpiry.messageManyExpired', { vmCount });
  }
  if (soonest === 0) {
    return t('bell.resourceExpiry.messageManyToday', { vmCount });
  }
  return t('bell.resourceExpiry.messageMany', { vmCount, count: soonest });
}

function NotificationRow({
  notification,
  onSelect,
}: {
  notification: Notification;
  onSelect: (notification: Notification) => void;
}) {
  const Icon = notificationIcons[notification.type];
  const iconColor = notificationColors[notification.type];
  const { title, message } = useDisplayContent(notification);
  const { i18n } = useTranslation();
  const dateLocale = i18n.language === 'vi' ? vi : enUS;

  return (
    <div
      className={cn(
        'px-4 py-3 hover:bg-accent transition-colors cursor-pointer',
        !notification.read && 'bg-accent/50',
      )}
      onClick={() => onSelect(notification)}
    >
      <div className="flex gap-3">
        <Icon className={cn('h-5 w-5 flex-shrink-0 mt-0.5', iconColor)} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium line-clamp-1">{title}</p>
          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
            {message}
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            {formatDistanceToNow(new Date(notification.timestamp), {
              addSuffix: true,
              locale: dateLocale,
            })}
          </p>
        </div>
        {!notification.read && (
          <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-2" />
        )}
      </div>
    </div>
  );
}

interface NotificationPanelProps {
  notifications: Notification[];
  /** Called when a row is clicked — mark-as-read and navigation are the caller's decision. */
  onSelect: (notification: Notification) => void;
  onMarkAllAsRead: () => void;
}

export function NotificationPanel({
  notifications,
  onSelect,
  onMarkAllAsRead,
}: NotificationPanelProps) {
  const { t } = useTranslation('notifications');
  const recentNotifications = notifications.slice(0, 10);

  if (recentNotifications.length === 0) {
    return (
      <div className="w-80 p-6">
        <div className="flex flex-col items-center justify-center text-center">
          <Bell className="h-12 w-12 text-muted-foreground mb-3" />
          <p className="text-sm font-medium">{t('bell.empty')}</p>
          <p className="text-xs text-muted-foreground mt-1">
            {t('bell.emptyHint')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-80">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <h3 className="font-semibold">{t('bell.title')}</h3>
        {notifications.some((n) => !n.read) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onMarkAllAsRead}
            className="h-auto py-1 px-2 text-xs"
          >
            {t('bell.markAllRead')}
          </Button>
        )}
      </div>

      {/* Notification List */}
      <ScrollArea className="h-[400px]">
        <div className="divide-y">
          {recentNotifications.map((notification) => (
            <NotificationRow
              key={notification.id}
              notification={notification}
              onSelect={onSelect}
            />
          ))}
        </div>
      </ScrollArea>

      {/* Footer */}
      <Separator />
      <div className="p-3">
        <Link to="/notifications">
          <Button variant="ghost" size="sm" className="w-full">
            {t('bell.viewAll')}
          </Button>
        </Link>
      </div>
    </div>
  );
}
